// @ts-check

import { unref, watchEffect } from 'vue';
import { useAuth } from './authPlugin.js';

/**
 * @typedef {import('vue-router').RouteLocation} RouteLocation
 */

/**
 * Stolen from https://github.com/auth0/auth0-vue/blob/0697f7a4e1d151ef95a315ff98354adb8db49381/src/utils.ts
 * Run watchEffect untill the watcher returns true, then stop the watch.
 * Once it returns true, the promise will resolve.
 * @param {Function} watcher Function to watch
 * @returns {Promise<any>} Returns promise
 */
function watchEffectOnceAsync(watcher) {
  return new Promise((resolve) => {
    watchEffectOnce(watcher, resolve);
  });
}

/**
 * Stolen from https://github.com/auth0/auth0-vue/blob/0697f7a4e1d151ef95a315ff98354adb8db49381/src/utils.ts
 * Run watchEffect untill the watcher returns true, then stop the watch.
 * Once it returns true, it will call the provided function.
 * @param {Function} watcher Function to watch
 * @param {Function} fn resolve function
 */
function watchEffectOnce(watcher, fn) {
  const stopWatch = watchEffect(() => {
    if (watcher()) {
      fn();
      stopWatch();
    }
  });
}

/**
 * @param {import('./authPlugin.js').UseAuth} client Auth client
 * @returns {Promise<boolean>} True if user is logged in
 */
const createGuardHandler = async (client) => {
  const fn = async () => {
    if (unref(client.isAuthenticated)) {
      return true;
    }

    client.logout();
    return false;
  };

  if (!unref(client.isLoading)) {
    return fn();
  }

  await watchEffectOnceAsync(() => !unref(client.isLoading));

  return fn();
};

/**
 * Waits for kinde to be called and then returns boolean for user logged in or not
 * @returns {Promise<boolean>} True if user is logged in
 */
export const authGuard = async () => {
  const auth = useAuth();
  await auth.ready();
  return createGuardHandler(auth);
};
